// Types
import { Booking, Order } from '@white-label-types/account-booking';
import {
  differenceInHours,
  parseISO,
} from '@white-label-helper/date-utilities';
import { BOOKING_STATUSES } from '@white-label-configuration/constants';

/**
 * Parses the `/account/orders/:channel_id` response data into an array of Booking objects
 * containing only the fields needed by the MA app.
 * @param bookings order data from `/account/orders/:channel_id` endpoint
 * @returns an array of Bookings containing only fields needed on MA app.
 */
export function parseOrdersResponse(bookings: Order[]): Booking[] {
  return bookings.map((booking: Order) => {
    const { reference, order_items } = booking;
    let isPrivateTerminalBooking = false;
    const orderItems = order_items.map((orderItem) => {
      const productOptionCode = orderItem?.inventory_option?.meta?.product_option_code
      if (
        orderItem?.order_inventory?.airline_name ||
        orderItem?.order_inventory?.baggage_type
      ) {
        isPrivateTerminalBooking = true;
      }

      let hasPassedDeadline = false;
      const direction = orderItem?.order_inventory?.direction;
      if (isPrivateTerminalBooking) {
        const flightTime =
          direction === 'inbound'
            ? orderItem.product.started_at.datetime
            : orderItem.product.closed_at.datetime;
        const hoursDiff = differenceInHours(parseISO(flightTime), new Date());
        hasPassedDeadline = hoursDiff < 24;
      }

      return {
        id: orderItem?.id,
        displayName: orderItem?.order_inventory?.display_name,
        direction,
        airlineName: orderItem?.order_inventory?.airline_name,
        baggageType: orderItem?.order_inventory?.baggage_type,
        startedAt: orderItem?.product?.started_at,
        closedAt: orderItem.product?.closed_at,
        address: orderItem?.product?.poi?.address,
        piiQuestionsStatus: orderItem?.pii_questions_data?.[0]?.status,
        productCode: orderItem?.product_code,
        productOptionCode,
        groups: {
          adult:
            orderItem?.inventory_option?.details?.groups?.adult?.totals
              ?.quantity ?? 0,
          child:
            orderItem?.inventory_option?.details?.groups?.child?.totals
              ?.quantity ?? 0,
          infant:
            orderItem?.inventory_option?.details?.groups?.infant?.totals
              ?.quantity ?? 0,
          senior:
            orderItem?.inventory_option?.details?.groups?.senior?.totals
              ?.quantity ?? 0,
        },
        hasPassedDeadline,
        status: orderItem?.status,
      };
    }).filter(Boolean);
    if (orderItems.length === 0) {
      return;
    }
    const orderItem = order_items[order_items.length - 1];
    const product = orderItem.product;
    const startDateTime = product?.started_at?.datetime.split('T');
    const closedDateTime = product?.closed_at?.datetime.split('T');

    const hasPIIQuestionData = orderItem?.pii_questions_data?.length > 0;

    const allOrderItemsAreCancelled = orderItems.every(
      (orderItem) => orderItem.status.toUpperCase() === BOOKING_STATUSES.CANCELLED
    );

    return {
      id: reference,
      product_code: orderItem?.product_code,
      ...(hasPIIQuestionData && {
        pii_questions_status: orderItem?.pii_questions_data?.[0]?.status,
      }),
      status: allOrderItemsAreCancelled ? BOOKING_STATUSES.CANCELLED : BOOKING_STATUSES.CONFIRMED,
      privateTerminalBooking: isPrivateTerminalBooking,
      startDatetime: {
        date: startDateTime ? startDateTime[0] : '',
        time: startDateTime ? startDateTime[1] : '',
      },
      endDatetime: {
        date: closedDateTime ? closedDateTime[0] : '',
        time: closedDateTime ? closedDateTime[1] : '',
      },
      orderItems: orderItems.filter(Boolean),
    };
  });
}
